<ng-container *ngIf="(path && path.length > 0) || (sequencedLegs && sequencedLegs.length > 0)">
  <div class="w-full desktop:h-[60dvh] relative mb-4" aria-label="Map Card">
    <ng-container *ngIf="path && path.length > 0; else noPathOrLoading">
      <div class="relative w-full h-[60dvh]">
        <google-map height="60dvh" width="100%" [center]="center" [zoom]="zoom" (mapInitialized)="onMapReady($event)"
          [options]="mapOptions">
          <map-polyline [path]="path" [options]="polylineOptions"></map-polyline>
          <!-- Start Marker -->
          <map-advanced-marker *ngIf="path && path.length > 0" [position]="path[0]" [title]="'Start Point'"
            [content]="startMarkerContent">
          </map-advanced-marker>
          <!-- End Marker -->
          <map-advanced-marker *ngIf="path && path.length > 1" [position]="path[path.length - 1]" [title]="'Last Point'"
            [content]="endMarkerContent">
          </map-advanced-marker>
          <!-- Drop Point Marker -->
          <map-advanced-marker *ngFor="let dropPoint of dropPoints" [position]="dropPoint"
            [title]="'Drop Point'"></map-advanced-marker>
        </google-map>
        <!-- Custom Zoom Controls -->
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex flex-col gap-2 items-center">
          <div class="flex items-center gap-2">
            <button nz-button nzType="default"
              class="w-10 h-10 flex items-center justify-center rounded-full shadow bg-white hover:bg-gray-100"
              (click)="zoomOut()" aria-label="Zoom Out">
              <i nz-icon nzType="minus" nzTheme="outline"></i>
            </button>
            <button nz-button nzType="default"
              class="w-10 h-10 flex items-center justify-center rounded-full shadow bg-white hover:bg-gray-100" (click)="zoomIn()"
              aria-label="Zoom In">
              <i nz-icon nzType="plus" nzTheme="outline"></i>
            </button>
            <button nz-button nzType="default"
              class="w-10 h-10 flex items-center justify-center rounded-full shadow bg-white hover:bg-gray-100" (click)="recenter()"
              aria-label="Recenter">
              <i nz-icon nzType="aim" nzTheme="outline"></i>
            </button>
          </div>
        </div>
      </div>
    </ng-container>
    <ng-template #noPathOrLoading>
      <ng-container *ngIf="path && path.length === 0 && isLoading; else noMapData">
        <div
          class="flex flex-col items-center justify-center h-[60dvh] w-full bg-gradient-to-br from-white to-gray-100 relative overflow-hidden"
          style="box-shadow: inset 0 2px 16px 0 rgba(0,0,0,0.06);">
          <span class="w-20 h-20 flex items-center justify-center">
            <i nz-icon nzType="loading" nzTheme="outline" class="text-3xl text-primary animate-spin"></i>
          </span>
          <span class="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2 relative z-10">
            <i nz-icon nzType="environment" nzTheme="twotone" class="text-primary text-xl"></i>
            Loading map...
          </span>
          <span class="text-sm text-gray-500 mb-2 text-center max-w-xs relative z-10">
            Fetching trip route, please wait.
          </span>
        </div>
      </ng-container>
      <ng-template #noMapData>
        <div
          class="flex flex-col items-center justify-center h-[60dvh] w-full bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden"
          style="box-shadow: inset 0 4px 24px 0 rgba(0,0,0,0.08);">
          <div class="bg-white rounded-full shadow-lg p-4 mb-4 animate-fade-in relative z-10"
            style="box-shadow: 0 2px 8px 0 rgba(0,0,0,0.10), inset 0 2px 12px 0 rgba(0,0,0,0.06);">
            <img src="assets/svg/path-not-found.svg" fetchpriority="high" decoding="async" alt="No Map Data" loading="eager"
              style="width: 8rem; height: 8rem; display: block;" width="128" height="128" importance="high"
              crossorigin="anonymous" />
          </div>
          <span class="text-2xl font-bold text-gray-700 mb-2 flex items-center gap-2 relative z-10">
            <i nz-icon nzType="environment" nzTheme="twotone" class="text-primary text-2xl"></i>
            No Map Data
          </span>
          <span class="text-base text-gray-500 mb-4 text-center max-w-md relative z-10">
            We couldn't find any map data for this trip.<br>
          </span>
        </div>
      </ng-template>
    </ng-template>
    <!-- Timeline Card floating on top right of the map -->
    <ng-container>
      <div class="absolute top-2 left-2 z-20">
        <nz-card *ngIf="lastLeg?.status !== 'completed'" [nzBodyStyle]="{ padding: '8px' }" class="glass-card w-full">
          <div class="flex items-center flex-col gap-y-2 max-h-[48dvh] overflow-y-auto">
            <!-- Vehicle Status -->
            <div role="text" class="flex flex-col items-center justify-center px-2 text-gray-600 border bg-emerald-50/50 border-gray-300 rounded-lg p-2 w-full"
              *ngIf="trip?.truck_status == 'stopped'">
              <i nz-icon nzType="hezky:truck_stopped" nzTheme="outline" class="text-4xl"></i>
              <span class="text-xs text-nowrap font-semibold">
                Idle Duration: ( {{ trip?.idle_duration | relativeTime:'card'}} )
              </span>
            </div>
            <!-- Door Status -->
            <div role="text" class="flex flex-col items-center justify-center px-2 text-danger border bg-emerald-50/50 border-gray-300 rounded-lg p-2 w-full" *ngIf="isDoorOpen">
              <i nz-icon nzType="hezky:door_open" nzTheme="outline" class="text-4xl"></i>
              <span class="text-xs text-nowrap font-semibold">Door Opened</span>
            </div>
            <!-- Inv movement -->
            <div role="text" class="flex flex-col items-center justify-center p-2 gap-x-2 text-danger border bg-emerald-50/50 border-gray-300 rounded-lg"
              *ngIf="trip?.status === 'paused'">
              <div class="flex flex-col items-center justify-center">
                <i nz-icon nzType="hezky:inv_started" nzTheme="outline" class="text-4xl"></i>
                <span class="text-xs text-nowrap font-semibold mb-2">Inv movement on</span>
              </div>
              <ng-container *ngFor="let template of alarmTemplate">
                <div class="flex flex-col items-center justify-center gap-y-1">
                  <ng-container *ngFor="let condition of ['Greater than','Less than']">
                    <div class="border border-gray-300 rounded-lg p-2 text-xs max-w-[150px] text-center text-gray-500"
                      *ngIf="template?.threshold_condition[condition]?.[0]?.inventory_threshold != null">
                      <div>
                        {{ condition }}
                      </div>
                      <span>
                        Inv threshold: {{
                        feUtil.getFormattedTemp(template?.threshold_condition[condition][0]?.inventory_threshold,template?.threshold_on || '') }}
                      </span>
                      <span>
                        Inv duration: {{ template?.threshold_condition[condition][0]?.inventory_duration | secondsToHuman:'short' }}
                      </span>
                    </div>
                  </ng-container>
                </div>
              </ng-container>
            </div>
          </div>
        </nz-card>
      </div>
      <div class="absolute top-2 right-2 z-20 min-w-[350px]">
        <div class="flex flex-nowrap w-full gap-x-2">
          <nz-card [nzBodyStyle]="{ padding: '8px' }" class="glass-card w-full">
            <div class="flex gap-x-2 items-center">
              <ng-container *ngIf="trip?.sensor as sensor">
                <ng-container *ngIf="lastLeg?.status !== 'completed' && lastLeg?.status !== 'discard';">
                  <ng-container *ngFor="let capability of trip?.capabilities">
                    <div class="flex flex-row gap-x-2 rounded-lg px-1.5 py-2 text-2xl">
                      <i nz-icon [nzType]="'hezky:' + capability?.charAt(0)?.toUpperCase() + capability?.slice(1).toLowerCase()"
                        nzTheme="outline" class="border-r border-gray-200 pr-1" [style.color]="feUtil.getColor('', capability)">
                      </i>
                      <span class="font-semibold leading-none"
                        [style.color]="trip?.active_incident_count > 0 ? '#e60707' : feUtil.getColor('', capability)">
                        {{ feUtil.getFormattedTemp(sensor?.readings?.[capability],capability) || '-' }}
                      </span>
                    </div>
                  </ng-container>
                </ng-container>
                <ng-container *ngIf="lastLeg?.status === 'completed' || lastLeg?.status === 'discard';">
                  <div class="text-sm font-semibold text-gray-800 flex items-center gap-x-1">
                    <span class="py-1 px-2 rounded-lg bg-white border border-gray-100">
                      <i nz-icon nzType="stock" nzTheme="outline"></i>
                    </span>
                    <span class="text-nowrap">Trip Statistics</span>
                  </div>
                  <ng-container *ngFor="let information of trip['tripHighLowInformation']">
                    <ng-container *ngIf="showCapabilities && showCapabilities.includes(information.label)">
                      <div class="flex flex-row gap-x-2 rounded-lg px-1.5 py-1 border border-white bg-gray-100">
                        <i nz-icon [nzType]="'hezky:' + information.label?.toLowerCase()" nzTheme="outline"
                          class="text-xl border-r border-gray-100 pr-1" [style.color]="feUtil.getColor('', information.label)">
                        </i>
                        <div class="flex flex-col items-center px-0.5 gap-y-1">
                          <span class="text-[10px] text-gray-500 leading-none">High</span>
                          <span class="font-semibold leading-none" [style.color]="feUtil.getColor('', information.label)">
                            {{ feUtil.getFormattedTemp(information.value.max, information.label) || '-' }}
                          </span>
                        </div>
          
                        <div class="flex flex-col items-center px-0.5 gap-y-1">
                          <span class="text-[10px] text-gray-500 leading-none">Avg</span>
                          <span class="font-semibold leading-none" [style.color]="feUtil.getColor('', information.label)">
                            {{ feUtil.getFormattedTemp(information.value.average, information.label) || '-' }}
                          </span>
                        </div>
          
                        <div class="flex flex-col items-center px-0.5 gap-y-1">
                          <span class="text-[10px] text-gray-500 leading-none">Low</span>
                          <span class="font-semibold leading-none" [style.color]="feUtil.getColor('', information.label)">
                            {{ feUtil.getFormattedTemp(information.value.min, information.label) || '-' }}
                          </span>
                        </div>
                      </div>
                    </ng-container>
                  </ng-container>
                </ng-container>
              </ng-container>
            </div>
          </nz-card>
          <nz-card *ngIf="isViewReport" class="glass-card w-full">
            <div class="flex items-center justify-center gap-x-2 text-danger">
              <i nz-icon nzType="warning" nzTheme="outline" class="text-xl"></i>
              <span class="text-sm text-nowrap">
                Total Incident Count: {{ lastLeg?.stats?.incidents?.master_incident_count || 0 }}
              </span>
            </div>
          </nz-card>
          <nz-card *ngIf="lastLeg?.status !== 'completed' && lastLeg?.status !== 'discard'" [nzBodyStyle]="{ padding: '8px' }" class="glass-card w-full">
            <div class="flex flex-col items-center justify-center px-2"
              [ngClass]="{'text-danger': trip?.active_incident_count > 0, 'text-success': trip?.active_incident_count === 0}">
              <i nz-icon [nzType]=" trip?.active_incident_count > 0 ? 'warning' : 'check-circle'" nzTheme="outline" class="text-2xl"></i>
              <span class="text-xs text-nowrap">
                {{ trip?.active_incident_count > 0 ? 'Incident Count: ' + trip?.active_incident_count : 'No Incident' }}
              </span>
            </div>
          </nz-card>
        </div>
      </div>
      <div class="absolute top-20 right-2 z-20 min-w-[350px]" *ngIf="!isViewReport">
        <app-trip-timeline [trip]="trip" [lastLeg]="lastLeg" [sequencedLegs]="sequencedLegs" (refresh)="onRefresh.emit('REFRESH')" [encodedPolyline]="encodedOverViewPolyline"></app-trip-timeline>
      </div>
    </ng-container>
  </div>
</ng-container>